import { Button, Text } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { uploadFile } from "../../../api";
import { colors } from "../../../styles";
import { ISignUpCertificate } from "../../../types";
import { getSignUpErrorMessage } from "./errors";
import FileAttach from "./FileAttach";
import StepLayout, { FieldMessage } from "./StepLayout";

interface ICertificateInfoProps {
    progress: number;
    setCertificateData: React.Dispatch<React.SetStateAction<Partial<ISignUpCertificate>>>;
    onNext: () => void;
}

/* 약대생 전용. Certificate 객체(재학증명서)를 만든다. */

export default function CertificateInfo({ progress, setCertificateData, onNext }: ICertificateInfoProps) {

    /* 로딩 상태, 에러 메시지, 재학증명서 이미지 */

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [certificateImage, setCertificateImage] = useState<File | null>(null);

    /* 재학증명서 업로드 뮤테이션 */

    const uploadFileMutation = useMutation({
        mutationFn: uploadFile,
    });

    /* 폼 제출 핸들러 */

    const onSubmit = async () => {

        /* 재학증명서 사진이 없으면 에러 메시지 설정 */

        if (!certificateImage) {
            setErrorMessage("재학 증명서 사진을 첨부해주세요.");
            return;
        }

        /* 로딩 상태, 에러 메시지 설정 */

        setIsLoading(true);
        setErrorMessage("");

        try {

            /* 재학증명서 업로드 */

            const image = await uploadFileMutation.mutateAsync({ file: certificateImage });

            /* 재학증명서 URL을 Certificate 객체로 저장 */
            setCertificateData({ image });

            /* 다음 화면으로 이동 */

            onNext();
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "재학 증명서 업로드 중 문제가 발생했습니다."));
        } finally {
            setIsLoading(false);
        }
    };

    /* render */

    return (
        <StepLayout
            progress={progress}
            titles={["증명 서류를 첨부해주세요."]}
            descriptions={["신청일 기준 6개월 이내 발급된 증명서만 가능합니다."]}
        >

            {/* 재학증명서 업로드 폼 */}

            <FileAttach
                label="01 | 재학 증명서"
                inputId="certificate-upload"
                file={certificateImage}
                onChange={(file) => {
                    setCertificateImage(file);
                    setErrorMessage("");
                }}
            />

            {/* 에러 메시지 */}

            <FieldMessage message={errorMessage} />

            {/* 다음 버튼 */}

            <Button
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={10}
                onClick={onSubmit}
                isLoading={isLoading}
                disabled={!certificateImage}
            >
                <Text fontSize={16} fontWeight={700}>다음</Text>
            </Button>
        </StepLayout>
    );
}
